import { enableDrag } from '../ui/draggable.js';
import { loadChat, sendChat, subscribeChat } from '../net/chat.js';

// Floating chat panel. Parallel to help.js / hands.js — same draggable
// pattern. Only active in online mode; session info is set via activate().

let gameId = '';
let mySeat = '';
let myName = '';
let messages = [];
let unsub = null;
let polling = null;
let initialized = false;
let unreadCount = 0;
let onUnreadChange = null;

export async function activateChat({ gameId: g, seat, displayName, onUnread }) {
  gameId = g;
  mySeat = seat;
  myName = displayName;
  onUnreadChange = onUnread || null;
  messages = [];
  unreadCount = 0;
  if (onUnreadChange) onUnreadChange(0);
  try {
    messages = await loadChat(gameId);
  } catch {
    /* 0 rows or permission issue — surface nothing, let it be empty */
  }
  if (unsub) unsub();
  unsub = subscribeChat(gameId, (msg) => {
    // Dedupe — we optimistically append our own send.
    if (messages.some((m) => m.id === msg.id)) return;
    messages.push(msg);
    if (isOpen() || msg.seat === mySeat) {
      // Seeing own messages, or panel is open — no unread increment.
    } else {
      unreadCount += 1;
      if (onUnreadChange) onUnreadChange(unreadCount);
    }
    render();
  });
  // Fallback poll in case realtime is silent (same concern as online-game).
  if (polling) clearInterval(polling);
  polling = setInterval(async () => {
    try {
      const rows = await loadChat(gameId);
      const seen = new Set(messages.map((m) => m.id));
      for (const r of rows) {
        if (!seen.has(r.id)) {
          messages.push(r);
          if (!isOpen() && r.seat !== mySeat) {
            unreadCount += 1;
            if (onUnreadChange) onUnreadChange(unreadCount);
          }
        }
      }
      render();
    } catch {
      /* transient */
    }
  }, 4000);
  render();
}

export function deactivateChat() {
  if (unsub) unsub();
  unsub = null;
  if (polling) clearInterval(polling);
  polling = null;
  gameId = '';
  mySeat = '';
  myName = '';
  messages = [];
  unreadCount = 0;
  if (onUnreadChange) onUnreadChange(0);
  render();
}

function isOpen() {
  return document.getElementById('chatOverlay')?.classList.contains('show');
}

export function showChat() {
  if (!gameId) return; // only meaningful in online mode
  document.getElementById('chatOverlay').classList.add('show');
  unreadCount = 0;
  if (onUnreadChange) onUnreadChange(0);
  setTimeout(() => document.getElementById('chatInput')?.focus(), 50);
  render();
}

export function hideChat() {
  document.getElementById('chatOverlay').classList.remove('show');
}

export function wireChat() {
  if (initialized) return;
  initialized = true;
  document.getElementById('btnChatClose').addEventListener('click', hideChat);
  document.getElementById('btnChatMin').addEventListener('click', () => {
    const box = document.getElementById('chatBox');
    const min = box.classList.toggle('minimized');
    document.getElementById('btnChatMin').textContent = min ? '+' : '–';
    document.getElementById('btnChatMin').title = min ? 'Expand' : 'Minimize';
  });
  document.getElementById('btnChatSend').addEventListener('click', submit);
  document.getElementById('chatInput').addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  });
  enableDrag({ panelId: 'chatBox', handleId: 'chatHead' });
}

async function submit() {
  const input = document.getElementById('chatInput');
  const text = (input.value || '').trim();
  if (!text || !gameId) return;
  input.value = '';
  // Optimistic local echo so Ken sees his message even if the realtime
  // round-trip is slow. The insert returns the real row id eventually; our
  // realtime dedupe by id keeps the feed from duplicating.
  const local = {
    id: -Date.now(), // sentinel negative so real rows don't collide
    seat: mySeat,
    display_name: myName,
    text,
    created_at: new Date().toISOString(),
    pending: true,
  };
  messages.push(local);
  render();
  try {
    await sendChat(gameId, { seat: mySeat, display_name: myName, text });
    // Next realtime/poll tick will add the real row; drop the local.
    messages = messages.filter((m) => m !== local);
    render();
  } catch (e) {
    local.pending = false;
    local.error = e?.message || 'send_failed';
    render();
  }
}

function render() {
  const root = document.getElementById('chatHistory');
  if (!root) return;
  if (messages.length === 0) {
    root.innerHTML = '<p style="color:var(--gray);font-size:.88rem">No messages yet. Say hi!</p>';
    return;
  }
  let h = '';
  for (const m of messages) {
    const mine = m.seat === mySeat;
    const cls = mine ? 'chat-msg mine' : 'chat-msg';
    const pending = m.pending ? ' <span class="chat-pending">sending…</span>' : '';
    const err = m.error ? ` <span class="chat-err">${escapeHtml(m.error)}</span>` : '';
    h += `<div class="${cls}"><span class="chat-who">${escapeHtml(m.display_name)}${mine ? ' (you)' : ''}</span><span class="chat-text">${escapeHtml(m.text)}${pending}${err}</span></div>`;
  }
  root.innerHTML = h;
  root.scrollTop = root.scrollHeight;
}

function escapeHtml(s) {
  return (s ?? '').toString().replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
}
